import { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { generatePath, withRouter } from 'react-router-dom';
import styled from 'styled-components';
import getComponentName from 'react-utils/getComponentName';
import { NavMarker } from 'react-rhumb';
import memoize from 'transmute/memoize';
import uniqueIdForObject from 'automation-ui-core/utils/uniqueIdForObject';
import { parseLocation } from 'automation-ui-routing/utils';
import { wireAuthConsumer } from 'automation-ui-core/context/AuthContext';
import { getActionOutputsForTargetActionById } from 'automation-ui-canvas/flow/records/Flow/flowUtils';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import * as UsageTrackingScreens from 'automation-ui-usage-tracking/UsageTrackingScreens';
import { trackEvent, trackPageView } from 'automation-ui-canvas/utils/trackingUtils';
import { ScreenTrackingContextProvider } from 'automation-ui-usage-tracking/ScreenTrackingContext';
import * as flowActions from 'automation-ui-canvas/actions/FlowActions';
import PannableDecorator from 'automation-ui-canvas/decorators/PannableDecorator';
import ZoomableDecorator from 'automation-ui-canvas/decorators/ZoomableDecorator';
import { wireActionOutputs } from 'automation-ui-action-features/queries/actionOutputQueries';
import { attachWrappedComponent } from 'automation-ui-core/decoratorUtils';
import { getSourceApp } from 'automation-ui-canvas/selectors/embedSelectors';
import FlowCanvasNode from 'automation-ui-canvas/components/canvas/FlowCanvasNode';
import { wireFlowById, wireUpdateFlow } from 'automation-ui-canvas/queries/flowQueries';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UIFixedSaveBar from 'UIComponents/panel/UIFixedSaveBar';
import * as messageActions from 'automation-ui-canvas/actions/MessageActions';
import { SAVED_WORKFLOW_FAILED } from 'automation-ui-canvas/constants/AlertTypes';
import OptimizationAlert from 'automation-ui-canvas/components/flow-header/OptimizationAlert';
import { CANVAS_ROOT } from 'automation-ui-canvas/constants/AbsolutePaths';
import { getScreenForFlowType } from 'automation-ui-canvas/utils/getScreenForFlowType';
import { buildSearchStringForActionSetRoutes } from 'automation-ui-canvas/utils/buildSearchStringForActionSetRoutes';
import { getZoomLevel } from 'automation-ui-canvas/selectors/localStorageSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledCanvas = styled.div.withConfig({
  displayName: "FlowCanvasOptimizeDecorator__StyledCanvas",
  componentId: "sc-1mn41om-0"
})(["height:100%;"]);
function getComposedCanvas() {
  return PannableDecorator(
  // @ts-expect-error ZoomableDecorator expects StyledCanvas to have its required props, but it's just a div
  // and the required props aren't wired until ComposedCanvas is used in FlowCanvasBase. Not sure if real error or not
  ZoomableDecorator({
    WrappedComponent: StyledCanvas
  }));
}
export default function FlowCanvasOptimizeDecorator(WrappedComponent, centerOnMount) {
  const componentName = getComponentName(WrappedComponent);
  class FlowCanvasOptimize extends Component {
    constructor(...args) {
      super(...args);
      this.state = {
        errorKey: ''
      };
      this.ComposedCanvas = getComposedCanvas();
      this.keydownEventListener = event => {
        const {
          key
        } = event;
        if (key === 'Escape') {
          this.redirectToFullCanvas();
        }
      };
      this.setEscapeKeyEventListener = () => {
        document.addEventListener('keydown', this.keydownEventListener);
      };
      this.handleGetActionOutputsForTargetActionById = (targetActionId, isNew) => getActionOutputsForTargetActionById({
        flow: this.props.flow,
        allOutputsByActionTypeId: this.props.allOutputsByActionTypeId,
        instanceOutputsByActionId: this.props.instanceOutputsByActionId,
        targetActionId,
        isNewAction: isNew
      });
      this.removeEscapeKeyEventListener = () => {
        document.removeEventListener('keydown', this.keydownEventListener);
      };
      this.getComposedProps = () => {
        const {
          canvasScrollElementRef,
          flow,
          zoomLevel
        } = this.props;
        return {
          flow,
          canvasScrollElementRef,
          onRenderCanvasNode: this.onRenderCanvasNode,
          onRenderConnectorButton: this.onRenderConnectorButton(),
          ComposedCanvas: this.ComposedCanvas,
          centerOnMount,
          zoomLevel
        };
      };
      this.onRenderCanvasNode = memoize(({
        id: nodeId,
        width,
        height
      }) => {
        const {
          flow,
          objectsInActions,
          listToStaticBranchNodeIds,
          rootBranchesToConsolidateNodeIds,
          zoomLevel
        } = this.props;
        const isHighlighted = listToStaticBranchNodeIds.has(String(nodeId)) || rootBranchesToConsolidateNodeIds.has(String(nodeId));
        return /*#__PURE__*/_jsx(FlowCanvasNode, {
          id: nodeId,
          width: width,
          height: height,
          flow: flow,
          isHighlighted: isHighlighted,
          isDisabled: !isHighlighted,
          readOnly: true,
          getActionOutputsForTargetActionById: this.handleGetActionOutputsForTargetActionById,
          objectsInActions: objectsInActions,
          zoomLevel: zoomLevel
        });
      }, ({
        id: nodeId
      }) => {
        return `${nodeId}-${uniqueIdForObject(this.props.flow)}-${this.props.flowId}-${this.props.actionId}-${this.props.zoomLevel}`;
      });
      this.onRenderConnectorButton = () => () => {
        return null;
      };
      this.finalizeOptimization = () => {
        const {
          sourceApp,
          updateFlow,
          flow,
          listToStaticBranchNodeIds,
          numOfCollapsedBranchActions,
          addAlert
        } = this.props;
        updateFlow({
          flow,
          sourceApp
        }).then(res => {
          trackEvent(Trackables.SAVED_OPTIMIZATION, {
            conversions: listToStaticBranchNodeIds.size,
            collapsed: numOfCollapsedBranchActions
          });
          if (res) {
            this.redirectToFullCanvas();
          }
        }).catch(() => {
          addAlert(SAVED_WORKFLOW_FAILED);
        });
      };
      this.redirectToFullCanvas = () => {
        const {
          flow,
          flowId,
          history,
          location,
          revertStagedChanges
        } = this.props;
        revertStagedChanges();
        const screen = getScreenForFlowType(flow.flowType);
        const pathname = generatePath(CANVAS_ROOT, {
          flowId,
          screen
        });
        const search = buildSearchStringForActionSetRoutes(flow, location);
        return history.replace({
          pathname,
          search
        });
      };
    }
    componentDidMount() {
      this.setEscapeKeyEventListener();
      if (!this.props.flow.stagedVersion) {
        this.redirectToFullCanvas();
      }
      trackPageView(UsageTrackingScreens.PLATFORM_FLOW_OPTIMIZATION_CONTAINER);
    }
    componentWillUnmount() {
      this.removeEscapeKeyEventListener();
    }
    render() {
      const {
        flow,
        numOfCollapsedBranchActions
      } = this.props;
      if (!flow) {
        return null;
      }
      return /*#__PURE__*/_jsx(NavMarker, {
        name: "OPTIMIZATION_CANVAS",
        children: /*#__PURE__*/_jsxs(ScreenTrackingContextProvider, {
          value: UsageTrackingScreens.PLATFORM_FLOW_OPTIMIZATION_CONTAINER,
          children: [/*#__PURE__*/_jsxs(UIFixedSaveBar, {
            children: [/*#__PURE__*/_jsx(UIButton, {
              onClick: this.finalizeOptimization,
              use: "primary",
              "data-test-id": "platform-settings-timing-save-button",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "automation.optimizeCanvas.save"
              })
            }), /*#__PURE__*/_jsx(UIButton, {
              onClick: this.redirectToFullCanvas,
              use: "secondary",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "platformSettings.cancelButton"
              })
            })]
          }), /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, this.props, this.getComposedProps(), {
            TopContent: /*#__PURE__*/_jsx(OptimizationAlert, {
              numOfCollapsedBranchActions: numOfCollapsedBranchActions,
              flow: flow
            })
          }))]
        })
      });
    }
  }
  const ConnectedFlowCanvasOptimize = compose(withRouter, wireAuthConsumer, _WrappedComponent => props => {
    const {
      flowId: stringFlowId
    } = props.match.params;
    const flowId = Number(stringFlowId);
    return /*#__PURE__*/_jsx(_WrappedComponent, Object.assign({}, props, {
      flowId: flowId
    }));
  }, wireFlowById, connect((state, ownProps) => {
    const {
      flowId,
      flow
    } = ownProps;
    const parsedQuery = parseLocation(ownProps.location);
    const {
      listToStaticBranchNodeIds = '',
      rootBranchesToConsolidateNodeIds = '',
      numOfCollapsedBranchActions = ''
    } = parsedQuery;
    return {
      flow,
      flowId,
      sourceApp: getSourceApp(state),
      listToStaticBranchNodeIds: new Set(listToStaticBranchNodeIds !== '' ? listToStaticBranchNodeIds.split(',') : undefined),
      rootBranchesToConsolidateNodeIds: new Set(rootBranchesToConsolidateNodeIds !== '' ? rootBranchesToConsolidateNodeIds.split(',') : undefined),
      numOfCollapsedBranchActions: Number(numOfCollapsedBranchActions),
      zoomLevel: getZoomLevel(flowId)(state)
    };
  }, {
    updateFlowLocally: flowActions.updateFlowLocally,
    revertStagedChanges: flowActions.revertStagedChanges,
    addAlert: messageActions.addAlert
  }), wireUpdateFlow, _WrappedComponent => props => wireActionOutputs(_WrappedComponent, {
    flow: props.flow
  })(props))(FlowCanvasOptimize);
  ConnectedFlowCanvasOptimize.displayName = `ConnectedFlowCanvasOptimize(${componentName})`;
  attachWrappedComponent(ConnectedFlowCanvasOptimize, WrappedComponent);
  return ConnectedFlowCanvasOptimize;
}